import Box from "@material-ui/core/Box";
import React from "react";
import styled from "styled-components";

interface PaginationProps {
  /**
   * Used to calculate the total number of pages
   * along with the per page item count
   */
  totalCount: number;
  /**
   * 1-based
   */
  currentPage: number;
  /**
   * Per page item count
   *
   * Used to calculate the total number of pages
   * along with the total item count
   */
  perPageCount: number;
  /**
   * function that returns a JSX element
   * given the current pagination parameters
   */
  renderPage: (pageNumber: number, isCurrentPage: boolean) => JSX.Element;
  /**
   * function that returns a JSX element
   * given the current pagination parameters
   */
  renderPrev: (currentPage: number) => JSX.Element;
  /**
   * function that returns a JSX element
   * given the current pagination parameters
   */
  renderNext: (currentPage: number, numPages: number) => JSX.Element;
  /**
   * Number of page links
   * < 1, 2, 3, 4, 5 > = 5
   */
  numPageLinks?: number;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  totalCount,
  currentPage,
  perPageCount,
  numPageLinks = 5,
  renderPage,
  renderPrev,
  renderNext,
  className,
}) => {
  /**
   * numPages: Number of pages derived from
   * total item count / per page item count
   */
  const numPages = Math.ceil(totalCount / perPageCount);
  /**
   * Final number of page links to display depend on
   * numPageLinks and numPages.
   * We can only show as many as minimum of the above two
   */
  const finalNumPageLinks = Math.min(numPageLinks, numPages);

  /**
   * Applies the mapper
   * in order to display the correct set of page links
   * depending on the current page
   *
   * currentPage = 5
   * finalNumPageLinks = 5
   * [3, 4, 5, 6, 7]
   */
  const pageLinkMapper =
    currentPage <= Math.ceil(finalNumPageLinks / 2)
      ? (idx) => idx + 1 // current page is in the left half of the page link set
      : (idx) =>
          Math.min(
            numPages - finalNumPageLinks + 1, // max amount of offset
            currentPage - Math.floor(finalNumPageLinks / 2), // offset needed to place current page in the center
          ) + idx;

  /**
   * Range serves as a basis for page links
   * we use the index of array as base page number
   *
   * finalNumPageLinks = 5
   * [0, 1, 2, 3, 4, 5]
   *
   * finalNumPageLinks = 3
   * [0, 1, 2]
   */
  const range = Array.from({
    length: finalNumPageLinks,
  }).map((_, idx) => pageLinkMapper(idx));

  return (
    <Box
      display="flex"
      alignItems="center"
      width="100%"
      justifyContent="center"
      className={className}
      my="40px"
    >
      {renderPrev(currentPage)}
      {range.map((pageNumber) =>
        renderPage(pageNumber, pageNumber === currentPage),
      )}
      {renderNext(currentPage, numPages)}
    </Box>
  );
};

export default React.memo(Pagination);

export const PageLink = styled.a<{ selected?: boolean; disabled?: boolean }>`
  border: none;
  background-color: transparent;

  ${({ disabled }) => disabled && "pointer-events: none"};
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  padding: 0 8px;
  ${({ selected }) => selected && `text-decoration: underline;`}
  &:hover {
    text-decoration: underline;
    color: inherit;
  }
  color: ${({ selected, theme }) =>
    selected ? theme.palette.primary.main : `inherit`};
`;

export const SiblingPageLink = styled.a<{ disabled?: boolean }>`
  border: none;
  background-color: transparent;

  ${({ disabled }) => disabled && "pointer-events: none; opacity: 0.25;"};
  font-weight: normal;
  font-size: 14px;
  line-height: 24px;
  padding: 0 6px;
  &:hover {
    text-decoration: underline;
    color: inherit;
  }
  color: inherit;
`;
